import { ClockIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSiteSettings } from "src/context/SiteSettingsContext";
import { WorkspaceContext } from "src/context/workspace";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import { apiRequest } from "src/async/apiUtils";
import { getNavigateLink } from "src/helpers/userHelper";
import { toast } from "react-toastify";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";

const WorkspacePaused = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspace_id } = useParams();
  const { site } = useSiteSettings();
  const { finishSwitching } = useWorkspaceSwitching();
  const { details: workspaceDetails } = useContext(WorkspaceContext);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    finishSwitching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { message, pause_expires_at } = location.state || {};
  const effectivePauseExpiresAt = pause_expires_at || workspaceDetails?.pause_expires_at;

  const handleCheckStatus = async () => {
    try {
      setCheckingStatus(true);
      const targetWorkspaceId = workspace_id || workspaceDetails?.slug || workspaceDetails?._id || localStorage.getItem("workspace");
      const { data: resData } = await apiRequest("GET", targetWorkspaceId ? `/portal/users/${targetWorkspaceId}/page` : `/portal/users/workspaces/page`, {}, { returnErrorWithstatus: true });

      if (resData.status === 200) {
        toast.success("Workspace is active now!");
        const navigate_link = getNavigateLink(resData);
        navigate(navigate_link);
      } else if (["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(resData?.code)) {
        toast.info(resData?.message || "Workspace is still paused.");
      } else if (resData?.code === "WORKSPACE_DEACTIVATED" || resData?.status === "inactive" || resData?.status === "client_inactive") {
        navigate("/client-inactive");
      } else {
        navigate("/permission-denied");
      }
    } catch (error) {
      const errData = error?.response?.data || error?.data;
      const errStatus = error?.response?.status || error?.status || errData?.status;
      const errCode = errData?.code;

      if ((errStatus === 403 || errData?.status === 403) && ["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(errCode)) {
        toast.info(errData?.message || "Workspace is still paused.");
      } else if (errStatus === 402 || errData?.status === 402) {
        navigate(`/workspace/${workspace_id || workspaceDetails?.slug || workspaceDetails?._id}/payment-required`);
      } else if (errCode === "WORKSPACE_DEACTIVATED" || errData?.status === "inactive" || errData?.status === "client_inactive") {
        navigate("/client-inactive");
      } else {
        toast.error("Failed to verify workspace status. Please try again.");
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  // const logoSrc = site?.image_logo;
  const highlightColor = site?.highlight_color || "#0062A2";

  return (
    <div className="fixed inset-0 flex h-screen w-screen flex-col items-center justify-between overflow-hidden bg-white p-6 font-sans text-slate-900 selection:bg-slate-900 selection:text-white sm:p-12">
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-[550px] w-[700px] -translate-x-1/2 rounded-full opacity-[0.12] blur-[150px]"
        style={{ backgroundColor: highlightColor }}
      />
      <div
        className="pointer-events-none absolute -bottom-40 left-1/2 h-[450px] w-[600px] -translate-x-1/2 rounded-full opacity-[0.08] blur-[150px]"
        style={{ backgroundColor: highlightColor }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)`,
          backgroundSize: "32px 32px",
        }}
      />

      <main className="relative z-10 my-auto flex w-full max-w-lg flex-col items-center text-center">
        <div className="relative mb-6 flex items-center justify-center">
          <div
            className="absolute h-24 w-24 animate-pulse rounded-full opacity-20 blur-xl"
            style={{ backgroundColor: highlightColor }}
          />
          <div
            className="relative flex h-24 w-24 items-center justify-center rounded-3xl border border-slate-100 bg-slate-50/80 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.04)]"
            style={{
              borderColor: `${highlightColor}30`,
            }}>
            <ClockIcon
              className="h-11 w-11"
              style={{ color: highlightColor }}
            />
          </div>
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Workspace Paused</h1>
        <p className="mt-3.5 max-w-md text-base font-normal leading-relaxed text-slate-500">{message || "This workspace has been temporarily paused. Please check back soon or contact your administrator."}</p>
        <div className="mt-8 w-full max-w-md">
          {effectivePauseExpiresAt ? (
            <div
              className="flex items-center justify-center gap-3 rounded-2xl border px-5 py-4 text-sm font-medium transition-all"
              style={{
                backgroundColor: `${highlightColor}08`,
                borderColor: `${highlightColor}25`,
                color: highlightColor,
              }}>
              <ClockIcon
                className="h-5 w-5 flex-shrink-0"
                style={{ color: highlightColor }}
              />
              <span>
                Expected to resume on: <strong className="font-semibold">{formatDate(effectivePauseExpiresAt)}</strong>
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3 rounded-2xl border border-slate-200/80 bg-slate-50/60 px-5 py-4 text-sm text-slate-600">
              <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-slate-400" />
              <span>Please contact your administrator for details.</span>
            </div>
          )}
        </div>
        <div className="mt-8 flex w-full max-w-xs justify-center">
          <button
            onClick={handleCheckStatus}
            type="button"
            disabled={checkingStatus}
            style={{
              backgroundColor: checkingStatus ? undefined : highlightColor,
              boxShadow: checkingStatus ? undefined : `0 10px 25px -5px ${highlightColor}40`,
            }}
            className={`group relative flex w-full items-center justify-center rounded-2xl px-8 py-3.5 text-base font-semibold text-white transition-all duration-200 focus:outline-none ${
              checkingStatus ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400" : "hover:scale-[1.02] hover:opacity-95 active:scale-[0.98]"
            }`}>
            <div className="flex items-center gap-2.5">
              {checkingStatus && (
                <Preloader
                  circleDimension="4"
                  text=""
                />
              )}
              <span>{checkingStatus ? "Verifying Status..." : "Check Status Again"}</span>
            </div>
          </button>
        </div>
      </main>
      {/* <footer className="relative z-10 text-xs text-slate-400">© ClearSquare. All rights reserved.</footer> */}
    </div>
  );
};

export default WorkspacePaused;
